import React, { useEffect, useState } from 'react'
import { Button, Card, Flex, IRenderControl, Modal, Table, toast } from '@fareye/ui'
import { useTranslation } from '@fareye/common'
import { cancelToken, get, isEmpty, post, put, del, getQueryParams } from '@fareye/utils'
import { FormBuilder } from '@fareye/form-builder'
import { Header } from '../../ChildUserFilter/components/Header'
import { childusers_config } from '../../../config/childusers_config'

type ChildUser = {
  id: number
  first_name?: string
  last_name?: string
  primary_phone_no?: string
  email?: string
  state?: string
  parent_id?: number
}

const CONFIG = childusers_config.sample as any
export default function ChildUsers({ match, location }: any) {
  const { t } = useTranslation()
  const parentUserId = match.params.userId
  const [jsonStoreConfig, setJsonStoreConfig] = useState(CONFIG)
  const [apiData, setApiData] = useState<IRenderControl>({
    data: [],
    message: '',
    isError: false,
    isLoading: false,
    isSuccess: true
  })

  const [selectedUser, setSelectedUser] = useState<ChildUser | null>(null)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const [searchEmail, setSearchEmail] = useState('')

  useEffect(() => {
    fetchJsonStoreConfig()
  }, [])

  async function fetchJsonStoreConfig() {
    try {
      const queryParam = getQueryParams({
        type: 'JsonStore::Ui',
        subType: 'child-users'
      })
      const jsonUrl = `/api/v1/records?${queryParam}`
      const { data } = await get(jsonUrl, cancelToken().source().token)
      const config = data.configs?.[0]?.data || CONFIG
      setJsonStoreConfig(config)
    } catch (ex) {
      setJsonStoreConfig(CONFIG)
    }
  }

  useEffect(() => {
    if (parentUserId) {
      fetchChildUsers()
    }
    return () => cancelToken().source().cancel('Component unmount in ChildUsers')
  }, [parentUserId])

  const handlePasswordSubmit = (formData: any) => {
    if (!isEmpty(formData?.values) && selectedUser?.id && !formSubmitted) {
      const { password, confirmPassword } = formData.values || {}
      if (password && confirmPassword) {
        setFormSubmitted(true)
        executeApi('updatePassword', selectedUser, formData)
      }
    }
  }

  useEffect(() => {
    if (selectedUser?.id) {
      setShowPasswordModal(true)
    }
  }, [selectedUser])

  async function fetchChildUsers() {
    const source = cancelToken().source()
    try {
      setApiData(prev => ({ ...prev, isLoading: true, isError: false }))
      const query = getQueryParams({ parent_id: parentUserId })
      const apiUrl = `${jsonStoreConfig.apiEndpoints.listUsers}?${query}`
      const resp = await get(apiUrl, source.token, jsonStoreConfig.apiConfig)
      const users: ChildUser[] = resp?.data?.child_users || []
      setApiData(prev => ({
        ...prev,
        isLoading: false,
        data: users
      }))
    } catch (ex) {
      setApiData(prev => ({
        ...prev,
        isLoading: false,
        isError: true
      }))
    }
  }

  async function executeApi(actionKey: string, user: any, formData?: any) {
  const action = jsonStoreConfig.actions[actionKey]
  if (!action) return
  if (action.type === 'api' || action.type === 'modal') {
    try {
      const methodMap: any = {
        POST: post,
        PUT: put,
        GET: get,
        DELETE: del
      }
      const apiMethod = methodMap[action.method] || post
      const payload: any = {}
      if (action.payload) {
        Object.keys(action.payload).forEach((key) => {
          const value = action.payload[key]  
          const formValue = formData?.values?.[value]
          if (formValue !== undefined) {
            payload[key] = formValue
          } else if (value === 'id') {
            payload[key] = user.id
          } else {
            payload[key] = user?.[value]
          }
        })
      }

      const url =
        jsonStoreConfig.apiEndpoints[action.apiKey] +
        (action.apiKey === 'updatePassword' ? `/${user.id}` : '')
      const resp = await apiMethod(
        url,
        payload,
        cancelToken().source().token,
        jsonStoreConfig.apiConfig
      )

      if (resp.status === 200) {
        toast.success(resp.data?.message || `${action.label} successful!`)
        fetchChildUsers()
        if (action.type === 'modal') {
          setShowPasswordModal(false)
          setSelectedUser(null)
          setFormSubmitted(false)
          setFormKey(prev => prev + 1)
        }
      } else {
        throw new Error()
      }
    } catch (err) {
      toast.error(`Failed to ${action.label.toLowerCase()}`)
      setFormSubmitted(false)
      setFormKey(prev => prev + 1)
    }
  }
}

  const renderCell = (_: string, config: any, row: any) => {
  if (config.fields) {
    return config.fields.map((f: string) => row?.[f] || '').join(' ')
  }
  if (config.field) {
    return row?.[config.field] || ''
  }
  return ''
}

const handleAction = (actionKey: string, user: any) => {
    const action = jsonStoreConfig.actions[actionKey as string] 
    if (action.type === 'modal') {
      setSelectedUser(user)
    } else if (action.type === 'api') {
      executeApi(actionKey, user)
    }
}

const columns = () => {
  return jsonStoreConfig.tableColumns.map((colKey: string) => {
    const config = jsonStoreConfig.columnConfig[colKey as string]

    if (!config) return null
    if (config.type === 'actions') {
      return {
        Header: t(jsonStoreConfig.labels.action as string),
        id: 'action',
        width: config.width,
        filterable: config.filterable,
        sortable: config.sortable,
        Cell: ({ row: { original } }: any) => (
          <Flex>
            {Object.keys(jsonStoreConfig.actions).map((actionKey: string) => {
              const action = jsonStoreConfig.actions[actionKey as string]
              return (
                <Button
                  key={actionKey}
                  ml={action.marginLeft}
                  variant={action.variant}
                  onClick={() => handleAction(actionKey, original)}
                >
                  {t(action.label)}
                </Button>
              )
            })}
          </Flex>
        )
      }
    }

    return {
      Header: t(jsonStoreConfig.labels[colKey as string]),
      id: colKey,
      width: config.width,
      filterable: config.filterable,
      sortable: config.sortable,
      Cell: ({ row: { original } }: any) => (
        <span>{renderCell(colKey, config, original)}</span>
      )
    }
  }).filter(Boolean)
}

  const tableData = ((apiData.data as any[]) || []).filter(item => {
    if (!searchEmail) return true
    return (item?.[jsonStoreConfig.searchField] || '').toLowerCase().includes(searchEmail.toLowerCase())
  })

  return (
    <Flex style={jsonStoreConfig.styling.container} column>
      <Flex alignItemsCenter justifyContentSpaceBetween>
        <h3>
          {jsonStoreConfig.labels.pageTitle} - {parentUserId}
        </h3>
      </Flex>

      <Card
        bg={jsonStoreConfig.styling.card.bg}
        header={<Header label={jsonStoreConfig.labels.headerLabel} setTextData={setSearchEmail} />}
      >
        <Table
          className={jsonStoreConfig.styling.table.className}
          columns={columns()}
          data={tableData}
          showTotalCount={jsonStoreConfig.styling.table.showTotalCount}
          sortable={jsonStoreConfig.styling.table.sortable}
          defaultPageSize={jsonStoreConfig.pageSize}
          dataLoading={apiData.isLoading}
          totalElements={tableData.length}
        />
      </Card>

      <Modal
        toggleModal={showPasswordModal}
        setToggleModal={(toggle: boolean) => {
          setShowPasswordModal(toggle)
          if (!toggle) setSelectedUser(null)
        }}
      >
        <Flex column style={{ padding: jsonStoreConfig.styling.modal.padding, minWidth: jsonStoreConfig.styling.modal.minWidth }}>
          <h3 style={{ marginTop: 0 }}>
            {jsonStoreConfig.labels.modalTitle} {selectedUser?.email ? `- ${selectedUser.email}` : ''}
          </h3>
          <FormBuilder
            key={formKey}
            setSubmitValues={handlePasswordSubmit}
            formDefaultValue={selectedUser || {}}
            config={jsonStoreConfig.formConfig as any}
            formGroupName="childUserPassword"
            isLoading={formSubmitted}
            baseUrl=""
          />
        </Flex>
      </Modal>
    </Flex>
  )
}
